/**
 * `gamification-core` — Bodega domain CONFIG (P-1, BYOS synchronizer).
 *
 * Retrofit OPT-IN and NON-punitive: rewards the HABIT of backing up (completed
 * syncs, consistency, conflict resolution, restorations) as
 * engagement — never surveillance. Private leagues by default (§leagues). The
 * engine is agnostic; here only sync domain rules are declared.
 *
 * Gamification DERIVES from the `activity` log (`entity_type =
 * "sync"` entries emitted by the BYOS engine — see `derive.ts`). It is a PURE and
 * recomputable projection: adds no tables and does not mutate the log.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";
import type { GameEvent } from "./types";

/** Domain event types (derived from sync auditing). */
export const BODEGA_EVENTS = {
  syncCompleted: "sync.completed",
  conflictResolved: "sync.conflict_resolved",
  versionRestored: "sync.restored",
} as const;

/** Streak line: days with at least one completed sync (consistency). */
export const BACKUP_STREAK_LINE = "backup";

/** XP cap for volume of files moved in a single sync. */
export const VOLUME_XP_CAP = 40;

/** XP of a sync: fixed base + extra capped by moved volume. */
export function syncCompletedXp(event: GameEvent): number {
  const moved = Math.max(0, Math.trunc(event.value ?? 0));
  return 10 + Math.min(moved, VOLUME_XP_CAP);
}

export const bodegaGamificationConfig: GamificationConfig = {
  xp: {
    [BODEGA_EVENTS.syncCompleted]: syncCompletedXp,
    [BODEGA_EVENTS.conflictResolved]: 15,
    [BODEGA_EVENTS.versionRestored]: 8,
  },
  streakLines: {
    [BODEGA_EVENTS.syncCompleted]: [BACKUP_STREAK_LINE],
  },
  counters: {
    [BODEGA_EVENTS.syncCompleted]: ["syncs"],
    [BODEGA_EVENTS.conflictResolved]: ["conflicts_resolved"],
    [BODEGA_EVENTS.versionRestored]: ["restores"],
  },
  badges: [
    {
      id: "first-sync",
      title: "First backup",
      description: "Complete your first sync.",
      when: counterAtLeast("syncs", 1),
    },
    {
      id: "ten-syncs",
      title: "Ten backups",
      description: "Complete ten syncs.",
      when: counterAtLeast("syncs", 10),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Sync seven days in a row.",
      when: streakAtLeast(BACKUP_STREAK_LINE, 7),
      bonusXp: 50,
    },
    {
      id: "peacemaker",
      title: "Peacemaker",
      description: "Resolve five sync conflicts.",
      when: counterAtLeast("conflicts_resolved", 5),
    },
    {
      id: "time-traveler",
      title: "Time traveler",
      description: "Restore a previous version.",
      when: counterAtLeast("restores", 1),
    },
  ],
  quests: [
    {
      id: "daily-sync",
      title: "Sync today",
      period: "daily",
      goals: [{ metric: BODEGA_EVENTS.syncCompleted, target: 1 }],
      bonusXp: 20,
    },
  ],
};

/** Named ranks by level — user's "title" according to their sync usage. */
export const BODEGA_RANKS = [
  { minLevel: 1, name: "Novice" },
  { minLevel: 3, name: "Keeper" },
  { minLevel: 6, name: "Guardian" },
  { minLevel: 10, name: "Archivist" },
  { minLevel: 15, name: "Vault Master" },
] as const;

/** Named rank for a given level (highest whose threshold is reached). */
export function rankForLevel(level: number): string {
  // The first rank is the guaranteed floor (minLevel = 1).
  let rank: string = BODEGA_RANKS[0].name;
  for (const tier of BODEGA_RANKS) {
    if (level >= tier.minLevel) {
      rank = tier.name;
    }
  }
  return rank;
}
